import PasswordPolicyChecklist from '@/components/PasswordPolicyChecklist';
import { updateCurrentUserPassword } from '@/services/user/passwordService';
import { getApiErrorMessage } from '@/utils/apiError';
import { logger } from '@/utils/logger';
import {
  evaluatePasswordPolicy,
  PASSWORD_POLICY_REGEX,
} from '@/utils/passwordPolicy';
import { Button, Form, Input, message } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';

type CurrentPasswordUpdateFormValues = {
  currentPassword: string;
  password: string;
  confirmPassword: string;
};

type CurrentPasswordUpdateFormProps = {
  submitText?: string;
  cancelText?: string;
  onCancel?: () => void;
  onSuccess?: (result: { password: string }) => void | Promise<void>;
  showCancelButton?: boolean;
};

const CurrentPasswordUpdateForm: React.FC<CurrentPasswordUpdateFormProps> = ({
  submitText = 'Actualizar',
  cancelText = 'Cancelar',
  onCancel,
  onSuccess,
  showCancelButton,
}) => {
  const [form] = Form.useForm<CurrentPasswordUpdateFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const newPasswordValue = Form.useWatch('password', form) ?? '';
  const passwordPolicyStatus = useMemo(
    () => evaluatePasswordPolicy(newPasswordValue),
    [newPasswordValue],
  );
  const isPasswordValid = useMemo(
    () => Object.values(passwordPolicyStatus).every(Boolean),
    [passwordPolicyStatus],
  );

  const shouldShowCancelButton = showCancelButton ?? Boolean(onCancel);

  const handleFinish = useCallback(
    async (values: CurrentPasswordUpdateFormValues) => {
      setIsSubmitting(true);
      try {
        const response = await updateCurrentUserPassword({
          current_password: values.currentPassword,
          password: values.password,
        });
        message.success(
          response?.message ?? 'Contraseña actualizada con éxito',
        );
        form.resetFields();
        await onSuccess?.({ password: values.password });
      } catch (error) {
        logger.error('Update password error', {
          scope: 'current_password_update_form',
          error,
        });
        message.error(
          getApiErrorMessage(
            error,
            'No pudimos actualizar tu contraseña. Por favor intentá nuevamente.',
          ),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, onSuccess],
  );

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel?.();
  }, [form, onCancel]);

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={handleFinish}
      autoComplete="off"
    >
      <Form.Item
        label="Contraseña actual"
        name="currentPassword"
        rules={[
          {
            required: true,
            message: 'Por favor ingresá tu contraseña actual',
          },
        ]}
      >
        <Input.Password autoComplete="current-password" />
      </Form.Item>
      <Form.Item
        label="Nueva contraseña"
        name="password"
        rules={[
          {
            required: true,
            message: 'Por favor ingresá tu nueva contraseña',
          },
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve();
              }
              if (PASSWORD_POLICY_REGEX.test(value)) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error('La contraseña no cumple con los requisitos'),
              );
            },
          },
        ]}
        validateStatus={
          newPasswordValue && !isPasswordValid ? 'error' : undefined
        }
        help={
          newPasswordValue && !isPasswordValid
            ? 'La contraseña no cumple con los requisitos'
            : undefined
        }
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <PasswordPolicyChecklist status={passwordPolicyStatus} />
      <Form.Item
        label="Confirmar nueva contraseña"
        name="confirmPassword"
        dependencies={['password']}
        rules={[
          {
            required: true,
            message: 'Por favor confirmá tu nueva contraseña',
          },
          ({ getFieldValue }) => ({
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve();
              }
              if (value === getFieldValue('password')) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Las contraseñas no coinciden'));
            },
          }),
        ]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          {shouldShowCancelButton && (
            <Button onClick={handleCancel}>{cancelText}</Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={!isPasswordValid}
          >
            {submitText}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default CurrentPasswordUpdateForm;
